import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as deviceActions from '../device/device.actions';
import { DeviceService } from '../../services/device/device.service';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from '../../../core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';

@Injectable()
export class DeviceEffects {
  getDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceActions.getDevice),
      switchMap(({ intervention, deviceUniqueId }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.deviceService.getDevice(transformedInterventionApi, deviceUniqueId).pipe(
          map((device) => {
            const newDeviceCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(device);
            return deviceActions.getDeviceSuccess({
              device: newDeviceCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(deviceActions.getDeviceError({ err })))
        );
      })
    )
  );

  getDeviceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.getDeviceError),
        tap(({ err }) => this.toastrService.error(`'Unable to save device tag!'`))
      ),
    { dispatch: false }
  );

  getDevices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceActions.getDevices),
      switchMap(({ intervention }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        return this.deviceService.getDevices(transformedInterventionApi).pipe(
          map((devices) => {
            const newDevicesListCamelCase = devices.map((device) =>
              this.dashboardCamelCasePipe.keysToCamelCase(device)
            );
            return deviceActions.getDevicesSuccess({
              devices: newDevicesListCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(deviceActions.getDevicesError({ err })))
        );
      })
    )
  );

  getDevicesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.getDevicesError),
        tap(({ err }) => this.toastrService.error(`Unable to save device tag!`))
      ),
    { dispatch: false }
  );

  editDeviceTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceActions.editDeviceTag),
      switchMap(({ intervention, device }) => {
        const transformedInterventionApi = this.dashboardSnakeCasePipe.keysToSnakeCase(intervention);
        const transformedDeviceApi = this.dashboardSnakeCasePipe.keysToSnakeCase(device);
        return this.deviceService.editDeviceTag(transformedInterventionApi, transformedDeviceApi).pipe(
          map((newDevice) => {
            const newDeviceCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newDevice);
            return deviceActions.editDeviceTagSuccess({
              device: newDeviceCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(deviceActions.editDeviceTagError({ err })))
        );
      })
    )
  );

  editDeviceTagError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.editDeviceTagError),
        tap(({ err }) => this.toastrService.error(`Unable to save device tag!`))
      ),
    { dispatch: false }
  );

  saveUnusedDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceActions.saveUnusedDevice),
      switchMap(({ interventionId, device }) => {
        const transformedDeviceApi = this.dashboardSnakeCasePipe.keysToSnakeCase(device);
        return this.deviceService.setUnusedForDevice(interventionId, transformedDeviceApi).pipe(
          map((newDevice) => {
            const newDeviceCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newDevice);
            return deviceActions.saveUnusedDeviceSuccess({
              device: newDeviceCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => of(deviceActions.saveUnusedDeviceError({ err })))
        );
      })
    )
  );

  saveUnusedDeviceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.editDeviceTagError),
        tap(({ err }) => this.toastrService.error(`Unable to save unused device!`))
      ),
    { dispatch: false }
  );

  connectingPahoClientFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.connectingMqttClientError),
        take(1),
        tap(() => {
          this.toastrService.error('Unable to connect to MQTT host');
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private deviceService: DeviceService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe
  ) {}
}
